/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kRestExecutor — abstract HTTP executor for SharePoint REST calls.
 *
 * Provides:
 *   RestRequestOptions  — typed options for a single HTTP request
 *   RestResponseError   — typed error result from a failed HTTP request
 *   kRestExecutor       — abstract class; subclasses implement transport + auth
 *
 * No browser APIs. Safe to import in Node.js, Azure Functions, or the browser.
 */

import { getCoreUtils } from "./kCoreHandler";

export class RestRequestOptions {
    public Method: string = "GET";
    public Url: string = "";
    public Data: string | null = null;
    public BinaryData: Blob | ArrayBuffer | null = null;
    public Headers: Record<string, string> = {};
    public IsBinaryResponse: boolean = false;
    // When true the executor returns the raw response text verbatim (no
    // JSON.parse). Used by endpoints such as .../Files('x')/$value that
    // return plain file content rather than an OData JSON envelope.
    public IsTextResponse: boolean = false;
    public IsExternal: boolean = false;
    /** Opt in for a request that cannot safely be replayed; false retains automatic retries. */
    public NoRetry: boolean = false;
    /**
     * OPT-IN bound on ONE attempt, in ms: no final answer (status AND body) by then = the attempt is
     * abandoned and fails with StatusCode 0 and `TimedOut` true. 0 (the default) = no bound, which is
     * what every caller had before 2026-09-24 - so a long upload is never cut off by a default.
     *
     * WHY IT EXISTS (SPArchive 1.0.2.0, live, /sites/SPArchive-Scale): one cold load never finished -
     * a RenderListDataAsStream page was issued and the next page never was, for 10 minutes. fetch has
     * NO timeout of its own, so a request the network or the server never answers strands its caller
     * for ever, silently. A caller that knows how long its request can reasonably take sets this, and
     * a hang becomes a failure it can show. Honoured by BrowserRestExecutor; other executors ignore
     * it (unchanged behaviour). RestRequestContext retries a timed-out attempt like any status-0
     * failure, and delivers the final one un-reported ONLY to a caller that set this AND gave an
     * ErrorHandler (it asked for the bound, so it owns the outcome).
     */
    public TimeoutMs: number = 0;
}

/**
 * The HTTP request-context fields an error may carry so the central handler can
 * name WHICH request failed. `RequestMethod` / `RequestPath` match the browser /
 * Graph convention (`kGraphCore._BindRequestContext` sets the same names), so the
 * shared error handlers read one uniform shape whether the error came from a
 * browser fetch, a Graph call, or a Node SharePoint call. All optional — an error
 * without request context simply omits them.
 */
export interface IHttpErrorContext {
    RequestMethod?: string;
    RequestPath?: string;
    StatusCode?: number;
}

export class RestResponseError {
    public StatusCode: number = 0;
    public ResponseText: string = "";
    /** The failing request's HTTP method + URL, populated by the executor at the
        point of failure so a report / log can name WHICH call failed (previously an
        error only carried the status + body, never the endpoint). */
    public Method: string = "";
    public Url: string = "";
    /** The server's Retry-After header, as sent ("120"), "" when it sent none. kRetryCore.ParseRetryAfter
        reads it first. Before 2026-09-24 the browser executor dropped the header, so a throttled request
        was retried after the 1/2/4 s backoff instead of the time SharePoint asked for, and ran out of
        retries inside the throttle window (SPArchive: 74 x 429 in one version trim, 11 surfaced). */
    public RetryAfter: string = "";
    /** Server-relative path the request was REDIRECTED to before it failed ("" = no redirect). fetch
        follows a 302 silently, so without this a failure answered by the redirect TARGET looks like the
        original endpoint's answer (SPArchive 2026-09-24: 817 x "302 then 406" on a GET that answers 200
        outside the burst). Path only - no origin, no query. */
    public RedirectedTo: string = "";
    /** True when the EXECUTOR abandoned the attempt because RestRequestOptions.TimeoutMs passed with
        no answer. StatusCode stays 0 - no server said anything - so this flag is the only way to tell
        "no answer in time" from a network refusal. */
    public TimedOut: boolean = false;

    /** Human-readable summary. Without this, a caught RestResponseError handed to
        the central error handler stringifies via String(e) as the useless
        "[object Object]" (it is a plain data class, not an Error), hiding the real
        cause.

        DOCUMENTED EXCEPTION: no error-mode guard and no try/catch, on purpose.
        toString() is invoked BY the error handler, so routing its own failure back
        through the handler would recurse; and an error-mode guard would blank the
        one line the report is being written to carry, at exactly the moment error
        mode is guaranteed to be on. Pure string concatenation over own fields
        cannot throw. */
    public toString(): string {
        let Text: string = "request failed";
        if (this.ResponseText !== "") { Text = this.ResponseText; }
        let Head: string = "RestResponseError";
        if (this.StatusCode !== 0) { Head = "RestResponseError HTTP " + this.StatusCode; }
        if (this.RedirectedTo !== "") { Head = Head + " after a redirect to " + this.RedirectedTo; }
        const Request: string = (this.Method + " " + this.Url).trim();
        if (Request !== "") { return Head + " [" + Request + "]: " + Text; }
        return Head + ": " + Text;
    }

    /**
     * Copy this error's request context onto another thrown value (typically a
     * fresh `Error` a caller wraps this into to get a stack). The central error
     * handler reads `RequestMethod` / `RequestPath` / `StatusCode` off whatever it
     * is given, so attaching them here lets a wrapped Error name the failing
     * endpoint while keeping the wrap's own (useful) stack at the raising site.
     *
     * ERROR-SUPPRESSED-JUSTIFIED: pure field copy invoked FROM an error path — it
     * must never re-enter KGlobalErrorHandler (would recurse) and cannot throw, so
     * it omits the guard/try-catch (same carve-out as toString above).
     */
    public AttachContextTo(target: object): void {
        const Ctx: IHttpErrorContext = target as IHttpErrorContext;
        Ctx.StatusCode = this.StatusCode;
        if (this.Method !== "") { Ctx.RequestMethod = this.Method; }
        if (this.Url !== "") { Ctx.RequestPath = this.Url; }
    }
}

/** A RestResponseError for a fetch-rejection reason: the reason's message when it is an
 *  Error, else its string form. One home for the wrap both fetch jobs do
 *  (Admin RestFetchJob.OnRejected / LangateSynch RequestJsonJob.Wrap, finding 14533).
 *  Callers that must pass an existing RestResponseError through unchanged keep their own
 *  early return before calling this. */
export const RestResponseErrorOf = (Reason: unknown): RestResponseError => {
    // DOCUMENTED EXCEPTION: pure error-object construction, no I/O; every caller keeps
    // its own error-mode gate, channel semantics and label.
    const Err: RestResponseError = new RestResponseError();
    Err.ResponseText = ReasonTextWithCause(Reason);
    return Err;
};

/** The text of a thrown / rejected value WITH its `cause` chain: "fetch failed: ECONNRESET read
 *  ECONNRESET". undici rejects every transport failure with the bare message "fetch failed" and keeps
 *  the reason (reset, DNS, TLS, refused, redirect loop) only in `cause` - a kErrors report that drops
 *  it cannot tell a network block from a deleted site (ExternalUsers reports, 2026-10-27). An
 *  AggregateError cause (undici's multi-address connect) contributes its first inner error. The head
 *  before the first colon stays the original message, so kRetryCore._ErrorCode still reads
 *  "fetch failed". Depth-limited against a cyclic chain. `cause` is read through a cast (lib ES2020). */
export const ReasonTextWithCause = (Reason: unknown): string => {
    // DOCUMENTED EXCEPTION: pure deterministic helper, no I/O - guarded at every call site.
    if (!(Reason instanceof Error)) { return String(Reason); }
    let Text: string = Reason.message;
    let Cause: unknown = (Reason as { cause?: unknown }).cause;
    for (let Depth: number = 0; Depth < 4 && Cause !== null && Cause !== undefined; Depth++) {
        Text = Text + ": " + CauseLinkText(Cause);
        if (!(Cause instanceof Error)) { break; }
        Cause = (Cause as { cause?: unknown }).cause;
    }
    return Text;
};

/** One link of a cause chain: "CODE message" (the code only when present and not already in the message). */
const CauseLinkText = (Cause: unknown): string => {
    // DOCUMENTED EXCEPTION: pure deterministic helper, no I/O - guarded at every call site.
    if (!(Cause instanceof Error)) { return String(Cause); }
    let Inner: Error = Cause;
    const Inners: unknown = (Cause as { errors?: unknown }).errors;
    if (Array.isArray(Inners) && Inners.length > 0 && Inners[0] instanceof Error) { Inner = Inners[0]; }
    let Code: string = CodeOf(Inner);
    if (Code === "") { Code = CodeOf(Cause); }
    let Message: string = Inner.message;
    if (Message === "") { Message = Cause.message; }
    if (Code === "" || Message.indexOf(Code) > -1) { return Message; }
    return Code + " " + Message;
};

/** A Node system error's string `code` (ECONNRESET, ENOTFOUND, UND_ERR_...), or "". */
const CodeOf = (Value: Error): string => {
    // DOCUMENTED EXCEPTION: pure deterministic helper, no I/O - guarded at every call site.
    const Code: unknown = (Value as { code?: unknown }).code;
    if (typeof Code !== "string") { return ""; }
    return Code;
};

export abstract class kRestExecutor {
    public SiteUrl: string = "";

    public abstract ExecuteRequest: (
        Options: RestRequestOptions,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        SuccessCallback: (Data: any) => void,
        ErrorCallback: (Error: RestResponseError) => void
    ) => void;

    public abstract GetAuthHeaders: (
        Method: string
    ) => Record<string, string>;

    public abstract FetchExternalDigest: (
        WebUrl: string,
        Callback: (Digest: string) => void
    ) => void;

    /**
     * Forget any digest this executor CACHED for `WebUrl`, so the next FetchExternalDigest asks the
     * server. Called before a "security validation" retry: the browser executor caches per web
     * (2026-09-24), and without this the retry would be handed back the very digest just refused.
     * Default: nothing cached, nothing to forget - executors that do not cache need not override.
     */
    public InvalidateExternalDigest = (_WebUrl: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            return;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kRestExecutor.InvalidateExternalDigest");
        }
    };
}
